import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { of, Observable } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ErrorHandlingService } from '../../services/error-handling/error-handling.service';
import { SpinnerService } from '../../services/spinner/spinner.service';
import { HttpHeaderConstants } from '../../services/http-header.constant';
import { Secret } from '../../models/appcloud/secret.model';

@Injectable({
    providedIn: 'root',
})
export class SecretService {
    readonly #errorHandlingService = inject(ErrorHandlingService);
    readonly #http = inject(HttpClient);
    readonly #spinnerService = inject(SpinnerService);

    private APPS_URL: string = environment.publicApiHost + '/appcloud/secrets';

    list(eloquainstallId: number, additionalHeaders: any = {}): Observable<Secret[]> {
        this.#spinnerService.addProcess(
            'AppService.listSecrets.' + eloquainstallId,
            'Retrieving your authentication information...'
        );
        const httpOptions = {
            headers: new HttpHeaders({
                ...HttpHeaderConstants.defaultHeaders,
                ...additionalHeaders,
            }),
            params: new HttpParams().set('eloquainstallId', eloquainstallId.toString()),
        };

        return this.#http.get<Secret[]>(this.APPS_URL, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while trying to retrieve authorization data. Please try again later.'
                );
                return of<Secret[]>();
            }),
            finalize(() => this.#spinnerService.completeProcess('AppService.listSecrets.' + eloquainstallId))
        );
    }

    get(secretId: number, additionalHeaders: any = {}): Observable<Secret> {
        this.#spinnerService.addProcess(
            'AppService.getSecret.' + secretId,
            'Retrieving your authentication information...'
        );
        const httpOptions = {
            headers: new HttpHeaders({
                ...HttpHeaderConstants.defaultHeaders,
                ...additionalHeaders,
            }),
        };

        return this.#http.get<Secret>(this.APPS_URL + '/' + secretId, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while trying to retrieve authorization data. Please try again later.'
                );
                return of<Secret>();
            }),
            finalize(() => this.#spinnerService.completeProcess('AppService.getSecret.' + secretId))
        );
    }

    create(secret: Secret, additionalHeaders: any = {}): Observable<Secret> {
        this.#spinnerService.addProcess('AppService.createSecret', 'Saving your authentication information...');
        const httpOptions = {
            headers: new HttpHeaders({
                ...HttpHeaderConstants.defaultHeaders,
                ...additionalHeaders,
            }),
        };

        return this.#http.post<Secret>(this.APPS_URL, secret, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while trying save your authorization data. Please try again later.'
                );
                return of<Secret>();
            }),
            finalize(() => this.#spinnerService.completeProcess('AppService.createSecret'))
        );
    }

    update(secretId: number, secret: Secret, additionalHeaders: any = {}): Observable<Secret> {
        this.#spinnerService.addProcess('AppService.updateSecret', 'Saving your authentication information...');
        const httpOptions = {
            headers: new HttpHeaders({
                ...HttpHeaderConstants.mergePatch,
                ...additionalHeaders,
            }),
        };

        return this.#http.patch<Secret>(this.APPS_URL + '/' + secretId, secret, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while trying to save your authorization data. Please try again later.'
                );
                return of<Secret>();
            }),
            finalize(() => this.#spinnerService.completeProcess('AppService.updateSecret'))
        );
    }
}
